import React, { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { dataService } from '@/lib/dataService'
import { dashboardService } from '@/lib/dashboardService'
import { useAuth } from '@/contexts/AuthContext'

export const DepartmentClassrooms: React.FC = () => {
  const { user, role } = useAuth()
  const [selectedDeptId, setSelectedDeptId] = useState<string>('')
  const [allDepts, setAllDepts] = useState<any[]>([])
  const [department, setDepartment] = useState<any>(null)
  const [summaryData, setSummaryData] = useState<any[]>([])
  const [subjects, setSubjects] = useState<any[]>([])
  const [assignments, setAssignments] = useState<any[]>([])
  const [teachers, setTeachers] = useState<any[]>([])
  const [classrooms, setClassrooms] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  // Search & Filter
  const [searchParams] = useSearchParams()
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [filterSubjectId, setFilterSubjectId] = useState('all')
  const [collapsedTeachers, setCollapsedTeachers] = useState<Record<string, boolean>>({})

  // Modal State
  const [selectedSummaryAssignment, setSelectedSummaryAssignment] = useState<any>(null)

  const loadDeptData = async () => {
    if (!user) return
    setLoading(true)
    try {
      try {
        await dashboardService.syncEnrollments()
      } catch (syncErr) {
        console.warn('Sync enrollments failed silently:', syncErr)
      }
      const depts = await dataService.getDepartments()
      setAllDepts(depts)

      let deptId: number | null = null
      const allProfiles = await dataService.getProfiles()
      
      if (role === 'admin') {
        if (selectedDeptId) {
          deptId = Number(selectedDeptId)
        } else if (depts.length > 0) {
          deptId = depts[0].id
          setSelectedDeptId(String(depts[0].id))
        }
      } else {
        const currentProfile = allProfiles.find((p: any) => p.id === user.id)
        deptId = currentProfile?.department_id || null
      }

      if (deptId !== null) {
        const [
          allSubjects,
          allClassrooms,
          allAssignments,
          allSessions
        ] = await Promise.all([
          dataService.getSubjects(),
          dataService.getClassrooms(),
          dataService.getTeachingAssignments(),
          dataService.getAssessmentSessions()
        ])

        const activeSession = allSessions.find((s: any) => s.is_open)
        const sessionId = activeSession?.id || 1

        const summary = await dataService.getDepartmentSummary(deptId, sessionId)
        setSummaryData(summary)

        const currentDept = depts.find((d: any) => d.id === deptId)
        setDepartment(currentDept)

        const deptSubjects = allSubjects.filter((s: any) => s.department_id === deptId)
        setSubjects(deptSubjects)
        const deptSubjectIds = deptSubjects.map((s: any) => s.id)

        const deptAssignments = allAssignments.filter((a: any) => deptSubjectIds.includes(a.subject_id))
        setAssignments(deptAssignments)

        setTeachers(allProfiles)
        setClassrooms(allClassrooms)
      }
    } catch (err) {
      console.error('Error loading classroom status:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDeptData()
  }, [user, selectedDeptId])


  const getSubjectInfo = (id: number) => {
    const s = subjects.find(sub => sub.id === id)
    return s ? `${s.code} ${s.name}` : 'ไม่ทราบวิชา'
  }

  const getClassroomName = (id: number) => {
    const c = classrooms.find(cl => cl.id === id)
    return c ? `ม.${c.grade_level >= 7 ? c.grade_level - 6 : c.grade_level}/${c.room_number}` : 'ไม่ทราบห้อง'
  }

  // Filter assignments based on selected subject and search term
  const getFilteredAssignments = () => {
    let result = assignments
    if (filterSubjectId !== 'all') {
      result = result.filter(a => a.subject_id === Number(filterSubjectId))
    }
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase()
      result = result.filter(a => {
        const sub = subjects.find(s => s.id === a.subject_id)
        const room = classrooms.find(c => c.id === a.classroom_id)
        const teacher = teachers.find(p => p.id === a.teacher_id)
        
        const subMatch = sub ? (sub.code.toLowerCase().includes(term) || sub.name.toLowerCase().includes(term)) : false
        const roomName = room ? `ม.${room.grade_level >= 7 ? room.grade_level - 6 : room.grade_level}/${room.room_number}` : ''
        const roomMatch = roomName.toLowerCase().includes(term)
        const teacherMatch = teacher ? `${teacher.first_name} ${teacher.last_name}`.toLowerCase().includes(term) : false
        
        return subMatch || roomMatch || teacherMatch
      })
    }
    return result
  }

  const filteredAssigns = getFilteredAssignments()

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: 'var(--primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    )
  }

  if (!department && role !== 'admin') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700' }}>🏫 สถานะการส่งรายห้องเรียน</h1>
        </div>
        <div className="glassmorphism" style={{ padding: '40px', textAlign: 'center' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '20px' }}>⚠️</span>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>ไม่สามารถดึงข้อมูลกลุ่มสาระฯ ได้</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', maxWidth: '500px', margin: '0 auto 24px' }}>
            เนื่องจากบัญชีของคุณยังไม่ได้เชื่อมโยงกับกลุ่มสาระวิชาใด ๆ ในระบบ กรุณาอัปเดตกลุ่มสาระของคุณที่หน้าข้อมูลส่วนตัว หรือติดต่อผู้ดูแลระบบเพื่อแก้ไขสิทธิ์
          </p>
          <Link to="/department/profile" className="btn btn-primary">👤 ตั้งค่ากลุ่มสาระในหน้าข้อมูลส่วนตัว</Link>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Department Selector for Admin */}
      {role === 'admin' && allDepts.length > 0 && (
        <div className="glassmorphism" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-primary)' }}>
            📂 สลับกลุ่มสาระการเรียนรู้:
          </label>
          <select
            value={selectedDeptId}
            onChange={(e) => setSelectedDeptId(e.target.value)}
            className="input-field"
            style={{ width: '280px', margin: 0 }}
          >
            {allDepts.map((d: any) => (
              <option key={d.id} value={String(d.id)}>{d.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Header */}
      <div>
        <Link to="/department" style={{ fontSize: '13px', color: 'var(--primary)', display: 'inline-block', marginBottom: '8px', textDecoration: 'none' }}>← กลับหน้าแดชบอร์ดหลัก</Link>
        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>🏫 สถานะการส่งคะแนนรายห้องเรียน</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          กลุ่มสาระการเรียนรู้: <strong>{department?.name}</strong> | ตารางสรุปสถานะความครบถ้วนและการส่งอนุมัติคะแนนสอบแยกตามรายห้องเรียนและครูผู้สอน
        </p>
      </div>

      {/* Search and Filters */}
      <div className="glassmorphism" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '240px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', marginBottom: '6px', color: 'var(--text-secondary)' }}>
              วิชาเรียนในกลุ่มสาระ:
            </label>
            <select
              value={filterSubjectId}
              onChange={(e) => setFilterSubjectId(e.target.value)}
              className="input-field"
              style={{ width: '100%', background: 'var(--bg-input)' }}
            >
              <option value="all">แสดงทุกรายวิชา</option>
              {subjects.map(s => (
                <option key={s.id} value={String(s.id)}>{s.code} {s.name}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: 2, minWidth: '280px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', marginBottom: '6px', color: 'var(--text-secondary)' }}>
              ค้นหาข้อมูลสอน:
            </label>
            <input
              type="text"
              className="input-field"
              placeholder="ค้นหารหัสวิชา, ห้องเรียน (ม.2/2) หรือชื่อครู..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1, background: 'var(--bg-input)' }}
            />
          </div>
        </div>
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>
          พบรายการสอนทั้งหมด {filteredAssigns.length} รายการ
        </span>
      </div>

      {/* Classroom Status Grid */}
      <div className="glassmorphism" style={{ padding: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>📋 สถานะความครบถ้วนแยกรายห้องเรียน</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              type="button" 
              onClick={() => {
                const uniqueTeacherIds = Array.from(new Set(filteredAssigns.map(a => a.teacher_id)))
                const newState: Record<string, boolean> = {}
                uniqueTeacherIds.forEach(id => { newState[id] = true })
                setCollapsedTeachers(newState)
              }} 
              className="btn btn-outline" 
              style={{ padding: '6px 12px', fontSize: '12px' }}
            >
              ➖ ย่อทั้งหมด
            </button>
            <button 
              type="button" 
              onClick={() => {
                setCollapsedTeachers({})
              }} 
              className="btn btn-outline" 
              style={{ padding: '6px 12px', fontSize: '12px' }}
            >
              ➕ ขยายทั้งหมด
            </button>
          </div>
        </div>

        {(() => {
          const uniqueTeacherIds = Array.from(new Set(filteredAssigns.map(a => a.teacher_id)))
          if (uniqueTeacherIds.length === 0) {
            return <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '24px' }}>📭 ไม่พบข้อมูลรายการส่งคะแนน</p>
          }
          return uniqueTeacherIds.map(teacherId => {
            const teacher = teachers.find(t => t.id === teacherId)
            const teacherName = teacher ? `${teacher.first_name} ${teacher.last_name}` : 'ไม่ทราบรายชื่อผู้สอน'
            const teacherAssigns = filteredAssigns.filter(a => a.teacher_id === teacherId)
            const isCollapsed = !!collapsedTeachers[teacherId]

            return (
              <div key={teacherId} style={{ marginBottom: '20px', borderBottom: '1px dashed var(--border)', paddingBottom: '20px' }}>
                <div 
                  onClick={() => setCollapsedTeachers(prev => ({ ...prev, [teacherId]: !prev[teacherId] }))}
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    background: isCollapsed ? 'rgba(59, 130, 246, 0.02)' : 'rgba(59, 130, 246, 0.05)',
                    border: '1px solid rgba(59, 130, 246, 0.1)',
                    marginBottom: isCollapsed ? '0px' : '14px',
                    transition: 'all 0.2s'
                  }}
                  className="hover-bg-change"
                >
                  <h3 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    👨‍🏫 ครูผู้สอน: {teacherName} ({teacherAssigns.length} ห้องเรียนที่รับผิดชอบ)
                  </h3>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {isCollapsed ? '➕ ขยายข้อมูล' : '➖ ย่อข้อมูล'}
                  </span>
                </div>

                {!isCollapsed && (
                  <div className="custom-table-container" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table className="custom-table" style={{ minWidth: '640px' }}>
                      <thead>
                        <tr>
                          <th style={{ minWidth: '120px' }}>วิชาเรียน</th>
                          <th style={{ minWidth: '80px', whiteSpace: 'nowrap' }}>ห้องเรียน</th>
                          <th style={{ textAlign: 'center', minWidth: '110px' }}>ความครบถ้วน<br/>Pre-Test</th>
                          <th style={{ textAlign: 'center', minWidth: '110px' }}>ความครบถ้วน<br/>Post-Test</th>
                          <th style={{ textAlign: 'center', minWidth: '100px' }}>การส่งอนุมัติ</th>
                          <th style={{ textAlign: 'center', minWidth: '90px' }}>ข้อมูลคะแนน</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teacherAssigns.map((assignment) => {
                          const summary = summaryData.find(s => s.assignmentId === assignment.id)
                          const studentCount = summary ? summary.totalStudents : 0
                          const preEntered = summary ? summary.preEnteredCount : 0
                          const postEntered = summary ? summary.postEnteredCount : 0
                          const status = {
                             pre: summary ? summary.preStatus : 'draft',
                             post: summary ? summary.postStatus : 'draft'
                          }

                          return (
                            <tr key={assignment.id}>
                              <td><strong>{getSubjectInfo(assignment.subject_id)}</strong></td>
                              <td><span style={{ fontWeight: '600' }}>{getClassroomName(assignment.classroom_id)}</span></td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                  <span style={{
                                    background: preEntered === studentCount && studentCount > 0 ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                                    border: `1px solid ${preEntered === studentCount && studentCount > 0 ? 'var(--success)' : 'var(--danger)'}`,
                                    color: preEntered === studentCount && studentCount > 0 ? 'var(--success)' : 'var(--danger)',
                                    padding: '3px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: '700'
                                  }}>
                                    {preEntered === studentCount && studentCount > 0 ? '✓ ครบถ้วน' : `⏳ ขาด ${studentCount - preEntered} คน`}
                                  </span>
                                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>({preEntered} / {studentCount} คน)</span>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                  <span style={{
                                    background: postEntered === studentCount && studentCount > 0 ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                                    border: `1px solid ${postEntered === studentCount && studentCount > 0 ? 'var(--success)' : 'var(--danger)'}`,
                                    color: postEntered === studentCount && studentCount > 0 ? 'var(--success)' : 'var(--danger)',
                                    padding: '3px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: '700'
                                  }}>
                                    {postEntered === studentCount && studentCount > 0 ? '✓ ครบถ้วน' : `⏳ ขาด ${studentCount - postEntered} คน`}
                                  </span>
                                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>({postEntered} / {studentCount} คน)</span>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                  <span style={{
                                    background: status.pre === 'submitted' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                                    border: `1px solid ${status.pre === 'submitted' ? 'var(--success)' : 'var(--warning)'}`,
                                    color: status.pre === 'submitted' ? 'var(--success)' : 'var(--warning)',
                                    padding: '2px 8px',
                                    borderRadius: '20px',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}>
                                    Pre: {status.pre === 'submitted' ? 'ส่งแล้ว' : 'ดราฟต์'}
                                  </span>
                                  <span style={{
                                    background: status.post === 'submitted' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                                    border: `1px solid ${status.post === 'submitted' ? 'var(--success)' : 'var(--warning)'}`,
                                    color: status.post === 'submitted' ? 'var(--success)' : 'var(--warning)',
                                    padding: '2px 8px',
                                    borderRadius: '20px',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}>
                                    Post: {status.post === 'submitted' ? 'ส่งแล้ว' : 'ดราฟต์'}
                                  </span>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {role === 'admin' ? (
                                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                                    <button 
                                      className="btn btn-outline" 
                                      style={{ padding: '6px 10px', fontSize: '11px', whiteSpace: 'nowrap' }}
                                      onClick={() => setSelectedSummaryAssignment(summary)}
                                    >
                                      📊 สรุป
                                    </button>
                                    <Link 
                                      to={`/teacher/score/${assignment.id}`} 
                                      className="btn btn-outline" 
                                      style={{ padding: '6px 10px', fontSize: '11px', whiteSpace: 'nowrap' }}
                                    >
                                      👁️ คะแนน
                                    </Link>
                                  </div>
                                ) : (
                                  <button 
                                    className="btn btn-outline" 
                                    style={{ padding: '6px 12px', fontSize: '12px' }}
                                    onClick={() => setSelectedSummaryAssignment(summary)}
                                  >
                                    📊 ดูสรุปการส่ง
                                  </button>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          })
        })()}
      </div>

      {/* Summary Modal Dialog */}
      {selectedSummaryAssignment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px'
        }}>
          <div className="glassmorphism" style={{
            width: '100%',
            maxWidth: '520px',
            borderRadius: '24px',
            border: '1px solid var(--border)',
            background: 'var(--bg-card)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            color: 'var(--text-primary)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '24px 24px 20px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>📊 สรุปผลการกรอกคะแนน</h3>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
                  ห้องเรียน: {selectedSummaryAssignment.classroomName} | วิชา: {selectedSummaryAssignment.subjectCode} {selectedSummaryAssignment.subjectName}
                </p>
              </div>
              <button 
                onClick={() => setSelectedSummaryAssignment(null)}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: 'none',
                  color: 'var(--text-primary)',
                  fontSize: '20px',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.1)', padding: '16px', borderRadius: '16px', textAlign: 'center' }}>
                  <span style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600', marginBottom: '4px' }}>เฉลี่ย Pre-Test</span>
                  <span style={{ fontSize: '24px', fontWeight: '800', color: 'var(--primary)' }}>
                    {selectedSummaryAssignment.preEnteredCount > 0 ? selectedSummaryAssignment.preAvg.toFixed(2) : '—'}
                  </span>
                  <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    (บันทึกแล้ว {selectedSummaryAssignment.preEnteredCount} / {selectedSummaryAssignment.totalStudents} คน)
                  </span>
                </div>
                <div style={{ background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.1)', padding: '16px', borderRadius: '16px', textAlign: 'center' }}>
                  <span style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600', marginBottom: '4px' }}>เฉลี่ย Post-Test</span>
                  <span style={{ fontSize: '24px', fontWeight: '800', color: 'var(--success)' }}>
                    {selectedSummaryAssignment.postEnteredCount > 0 ? selectedSummaryAssignment.postAvg.toFixed(2) : '—'}
                  </span>
                  <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    (บันทึกแล้ว {selectedSummaryAssignment.postEnteredCount} / {selectedSummaryAssignment.totalStudents} คน)
                  </span>
                </div>
              </div>

              {/* Learning Gain Summary */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border)',
                borderRadius: '16px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <span style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600', marginBottom: '6px' }}>
                  พัฒนาการเฉลี่ยห้องเรียน (Average Learning Gain)
                </span>
                <span style={{
                  fontSize: '28px',
                  fontWeight: '800',
                  color: selectedSummaryAssignment.learningGain >= 0 ? 'var(--success)' : 'var(--danger)'
                }}>
                  {selectedSummaryAssignment.preEnteredCount > 0 && selectedSummaryAssignment.postEnteredCount > 0 
                    ? `${selectedSummaryAssignment.learningGain >= 0 ? '+' : ''}${selectedSummaryAssignment.learningGain.toFixed(2)}`
                    : 'รอข้อมูลครบถ้วน'
                  }
                </span>
                <p style={{ fontSize: '11px', color: 'var(--text-secondary)', margin: '6px 0 0', lineHeight: 1.5 }}>
                  คำนวณจากคะแนนเฉลี่ยหลังเรียน หักลบด้วยคะแนนเฉลี่ยก่อนเรียนเพื่อวัดประสิทธิผลเชิงพัฒนาการรายบุคคลในห้องเรียนนี้
                </p>
              </div>

              {/* Status summary */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>ผู้สอนรายวิชา:</span>
                  <strong>{selectedSummaryAssignment.teacherName}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>สถานะการส่ง Pre-Test:</span>
                  <span style={{ color: selectedSummaryAssignment.preStatus === 'submitted' ? 'var(--success)' : 'var(--warning)', fontWeight: '700' }}>
                    {selectedSummaryAssignment.preStatus === 'submitted' ? '✓ อนุมัติส่งคะแนนแล้ว' : '⏳ รอดำเนินการ'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>สถานะการส่ง Post-Test:</span>
                  <span style={{ color: selectedSummaryAssignment.postStatus === 'submitted' ? 'var(--success)' : 'var(--warning)', fontWeight: '700' }}>
                    {selectedSummaryAssignment.postStatus === 'submitted' ? '✓ อนุมัติส่งคะแนนแล้ว' : '⏳ รอดำเนินการ'}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'flex-end',
              background: 'rgba(0, 0, 0, 0.05)'
            }}>
              <button 
                onClick={() => setSelectedSummaryAssignment(null)}
                className="btn btn-outline"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default DepartmentClassrooms
